import { AsyncModule } from 'vuex-async-mutations';
import cache from '@/services/image-cache';
import { downloadPath } from '@/utilities/files';
import { v4 as uuid } from 'uuid';
import { MediaItem, MediaType, ServerStatus } from '@/types';
import Vue from 'vue';
import { TrackJS } from 'trackjs';

type LibraryState = {
  items: MediaItem[];
  lastSync: number | null;
  serverItems: Record<string, boolean>;
  cacheItems: string[];
  isSyncing: boolean;
  syncing: string[];
};

const factory = (): LibraryState => ({
  items: [],
  lastSync: null,
  cacheItems: [],
  serverItems: {},
  isSyncing: false,
  syncing: [],
});

function getStatus(val?: boolean): ServerStatus {
  switch (val) {
    case true:
      return 'available';
    case false:
      return 'missing';
    default:
      return 'unknown';
  }
}

export const module: AsyncModule<LibraryState, any> = {
  state: factory,

  async: true,

  namespaced: true,

  getters: {
    ['isSaved'](state) {
      return (url: string) => {
        const item = state.items.find(item => item.url === url);

        return !item || !!item.saved;
      };
    },

    ['isSyncing'](state) {
      return state.isSyncing;
    },

    ['isItemSyncing'](state) {
      return (url: string) => state.syncing.includes(url);
    },

    ['findItem'](state) {
      return (url: string) => state.items.find(item => item.url === url);
    },

    ['imageExists'](state) {
      return (url: string): boolean => !!state.serverItems[url];
    },

    ['serverStatus'](state) {
      return (url: string): ServerStatus => getStatus(state.serverItems[url]);
    },

    ['imagesExist'](state) {
      return (urls: string[]): boolean => urls.every(url => !!state.serverItems[url]);
    },

    ['syncingImages'](state) {
      return state.syncing;
    },

    ['unsyncedImages'](state) {
      return (items: MediaItem[] = state.items) => items.filter(item => !item.saved);
    },

    ['availableInCache'](state) {
      return (url: string) => state.cacheItems.includes(url);
    },

    // A referenced photo the server does not have AND that this device cannot
    // re-upload from its local cache. This is the exact condition that blocks
    // finishing the module, so field/section warnings and the module-level
    // "niet beschikbaar op de server" banner all resolve through this one rule.
    ['missingOnServer'](_state, getters) {
      return (url: string): boolean =>
        getters.serverStatus(url) === 'missing' && !getters.availableInCache(url);
    },
  },

  mutations: {
    ['reset'](state) {
      Object.assign(state, factory());
    },

    ['empty'](state) {
      state.isSyncing = false;
      state.syncing = [];
    },
  },

  actionsAsync: {
    ['getCacheItems']: {
      async handler({ state, commitAsync }) {
        commitAsync(cache.all());
      },

      resolved(state, reqs: Request[]) {
        state.cacheItems = reqs.map(req => req.url.replace(window.location.origin, ''));
      },
    },

    ['createMissingImagesFromCache']: {
      async handler({ state, commitAsync }, urls: string[]) {
        return urls
          .map(url => {
            const original = state.items.find(item => item.url === url);

            if (original) return null;

            const name = url.split('/').pop();
            const type = name!.split('.').pop() as MediaType;

            const item: MediaItem = {
              url,
              name: name!,
              type,
              original: name!,
              added: Date.now(),
              saved: state.serverItems[url] ? Date.now() : null,
              fromCache: true,
            };

            commitAsync(Promise.resolve(item));

            return item;
          })
          .filter(item => !!item);
      },

      resolved(state, item: MediaItem) {
        state.items.push(item);
      },
    },

    ['addBlob']: {
      handler(
        { commitAsync },
        {
          blob,
          type = 'png',
          original = null,
        }: { blob: Blob; type: MediaType; original: string | null },
      ) {
        const name = `${uuid()}.${type}`;

        return commitAsync(cache.add(name, blob), { original, type, name });
      },

      resolved(
        state,
        url: string,
        { original, type, name }: { original: string; type: MediaType; name: string },
      ) {
        state.items.push({
          url,
          name,
          type,
          original,
          added: Date.now(),
          saved: null,
        });
      },
    },

    ['remove']: {
      async handler({ commitAsync }, items: MediaItem[]) {
        for (let item of items) {
          await commitAsync(cache.remove(item.url), item);
        }
      },

      resolved(state, payload, item: MediaItem) {
        const index = state.items.findIndex(i => i.url === item.url);

        if (index < 0) {
          return;
        }

        state.items.splice(index, 1);
      },
    },

    ['save']: {
      async handler({ commitAsync, dispatch }, files: MediaItem[]) {
        if (!files.length) {
          return true;
        }

        const { type, name, url } = files.pop()!;

        try {
          const blob = await cache.match(url);
          const file = new File([blob], name, { type: `image/${type}` });

          const formdata = new FormData();
          formdata.append('file[]', file);

          await commitAsync(
            this.$axios.$post('/files/upload', formdata, { headers: { ['Content-Type']: false } }),
            url,
          );
        } catch (error) {
          if (error.response) {
            TrackJS.addMetadata('Response', error.response.data);
          }
        }

        return dispatch('save', files);
      },

      pending(state, url: string) {
        state.syncing.push(url);
      },

      resolved(state, payload, url: string) {
        const item = state.items.find(item => item.url === url);

        if (!item) return;

        item.saved = Date.now();
      },

      finally(state, url: string) {
        const index = state.syncing.indexOf(url);

        if (index >= 0) {
          state.syncing.splice(index, 1);
        }
      },
    },

    ['sync']: {
      handler({ commitAsync, dispatch, state }, files?: MediaItem[]) {
        if (state.isSyncing) {
          return Promise.reject();
        }

        const items = files ?? state.items.filter(item => !item?.saved);

        return commitAsync(dispatch('save', items));
      },

      pending(state) {
        state.isSyncing = true;
      },

      resolved(state) {
        state.lastSync = Date.now();
      },

      finally(state) {
        state.isSyncing = false;
      },
    },

    ['verifyImage']: {
      async handler({ commitAsync }, url: string) {
        try {
          // Verify against the *current* API host: stored urls may carry a
          // retired host from a pre-cutover PWA cache, which now 301-redirects
          // and fails CORS preflight. downloadPath keeps only the filename so
          // the request resolves against axios' configured baseURL. The
          // original `url` stays the action key (state is indexed by it).
          await commitAsync(
            this.$axios.post(downloadPath(url), undefined, { params: { timestamp: Date.now() } }),
            url,
          );
          return true;
        } catch (e) {
          return false;
        }
      },

      resolved(state, payload, url: string) {
        const item = state.items.find(i => i.url === url);

        if (item && !item.saved) {
          item.saved = Date.now();
        }

        Vue.set(state.serverItems, url, true);
      },

      rejected(state, payload, url: string) {
        Vue.set(state.serverItems, url, false);
      },
    },
  },

  actions: {
    created: {
      root: true,
      handler({ commit }) {
        commit('empty');
      },
    },

    async ['getAllImagesFromCache']({ state }) {
      const reqs: Request[] = await cache.all();
      const urls: string[] = reqs.map(req => req.url.replace(window.location.origin, ''));

      return urls.filter(url => !state.items.find(item => item.url === url));
    },

    ['createAllMissingImagesFromCache']: {
      async handler({ dispatch }) {
        const reqs: Request[] = await cache.all();
        const urls: string[] = reqs.map(req => req.url.replace(window.location.origin, ''));

        return dispatch('createMissingImagesFromCache', urls);
      },
    },

    async ['verifyImages']({ dispatch, state }, urls: string[]) {
      const unique = urls.filter((url, index) => urls.indexOf(url) === index);

      for (let url of unique) {
        await dispatch('verifyImage', url);
      }
    },

    ['addFile']({ state, dispatch }, file: File) {
      const duplicate = state.items.find(item => item.original === file.name);

      if (duplicate) {
        return duplicate.url;
      }

      const type = file.name.split('.').pop() as MediaType;

      return dispatch('addBlob', { blob: file, type, original: file.name });
    },

    ['addPhoto']({ dispatch }, file: File) {
      const type = file.name.split('.').pop() as MediaType;

      return dispatch('addBlob', { blob: file, type, original: `camera-${uuid()}.${type}` });
    },

    ['reset']: {
      root: true,
      handler({ commit }) {
        commit('reset');
      },
    },
  },
};

export default module;
